import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from '../auth/useTenantContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

/** Assegnazione di una sottolista: a un operatore o al pool self-claim. */
export type CriteriaAssign = { operator_user_id: number } | { pool: true };

/** Regola di segmentazione (SP-6d), applicata in cascata. */
export type CriteriaRule =
  // SP-8x — qty_band può distribuire i titoli per quantità su più operatori.
  | { type: 'qty_band'; min: number | null; max: number | null; assign: CriteriaAssign | { operator_user_ids: number[] } }
  | {
      type: 'every_n_titles';
      n: number;
      order_by_location?: boolean;
      // every_n può distribuire i blocchi round-robin su più operatori.
      assign: CriteriaAssign | { operator_user_ids: number[] };
    };

export type SplitCmd =
  | { mode: 'manual'; missions: Array<{ operator_user_id: number; tasks: Array<{ pick_list_item_id: number; qty_assigned: number }> }> }
  | { mode: 'auto_qty'; operator_user_ids: number[] }
  | { mode: 'self_claim'; missions_count: number }
  | {
      mode: 'criteria';
      criteria: CriteriaRule[];
      // split-criteria-ean-atomic: il residuo accetta anche più operatori
      // (distribuzione per copie a titoli/EAN interi via EanBalancedDistributor).
      residual?: { order_by_location?: boolean; assign: CriteriaAssign | { operator_user_ids: number[] } };
    };

export interface SplitResponse {
  missions: Array<{ id: number; status: string; tasks_count: number }>;
}

export function useSplitPickList(token: string | null, tenantCode: string | null, pickListId: number | null) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (cmd: SplitCmd): Promise<SplitResponse> => {
      if (!token || !tenantCode || pickListId === null) {
        throw new Error('Missing auth context');
      }
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'X-Tenant-Id': tenantCode,
        'Content-Type': 'application/json',
        // Backend requires an Idempotency-Key on every mutating split call.
        // crypto.randomUUID is available in every browser this dashboard
        // targets (Chromium 92+ / Firefox 95+), so no fallback needed.
        'Idempotency-Key': crypto.randomUUID(),
      };
      if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);
      const res = await fetch(`${API_BASE}/admin/pick-lists/${pickListId}/split`, {
        method: 'POST',
        headers,
        body: JSON.stringify(cmd),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw Object.assign(new Error(`${res.status}: ${txt}`), {
          status: res.status,
          body: tryParseJson(txt),
        });
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'detail', pickListId] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'list'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
      // Uno split crea (e con self_claim/criteria/manual ANCHE assegna) missioni:
      // la board Assegnazioni e i contatori pick-list devono rinfrescarsi.
      // Prefissi generici → prefix-match tanstack: coprono la board/counts di
      // qualunque warehouse in cache, senza dipendere dal warehouseId corrente.
      qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
    },
  });
}

function tryParseJson(s: string): unknown {
  try { return JSON.parse(s); } catch { return null; }
}
